<script setup lang="ts">
const { t } = useI18n();
const localePath = useLocalePath();

useHead({
  titleTemplate: () => t("intentPages.viberBusinessMessagesProvider.pageTitle"),
  meta: [
    { name: "description", content: () => t("intentPages.viberBusinessMessagesProvider.pageMeta") },
    { property: "og:title", content: () => t("intentPages.viberBusinessMessagesProvider.pageTitle") },
    { property: "og:description", content: () => t("intentPages.viberBusinessMessagesProvider.pageMeta") },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "SMSBAT Viber Messaging Platform",
        "applicationCategory": "BusinessApplication",
        "description": "Platform for sending Viber Business Messages including OTP, transactional and promotional notifications",
        "operatingSystem": "API"
      })
    }
  ]
});

const faqItems = ref([
  { question: t('intentPages.viberBusinessMessagesProvider.faq.q1'), answer: t('intentPages.viberBusinessMessagesProvider.faq.a1'), isOpen: false },
  { question: t('intentPages.viberBusinessMessagesProvider.faq.q2'), answer: t('intentPages.viberBusinessMessagesProvider.faq.a2'), isOpen: false },
  { question: t('intentPages.viberBusinessMessagesProvider.faq.q3'), answer: t('intentPages.viberBusinessMessagesProvider.faq.a3'), isOpen: false },
  { question: t('intentPages.viberBusinessMessagesProvider.faq.q4'), answer: t('intentPages.viberBusinessMessagesProvider.faq.a4'), isOpen: false },
  { question: t('intentPages.viberBusinessMessagesProvider.faq.q5'), answer: t('intentPages.viberBusinessMessagesProvider.faq.a5'), isOpen: false },
  { question: t('intentPages.viberBusinessMessagesProvider.faq.q6'), answer: t('intentPages.viberBusinessMessagesProvider.faq.a6'), isOpen: false }
]);

onMounted(() => {
  document.querySelector("body")?.scrollIntoView();
});
</script>

<template>
  <div class="provider-intent-page">
    
    <!-- New Hero Banner -->
    <div class="main-banner bg-gradient-to-br from-indigo-50 to-indigo-100 pt-[100px] pb-12 px-4">
      <div class="container mx-auto max-w-5xl text-center">
        <h1 class="text-4xl md:text-6xl font-extrabold text-[#3070b6] mb-6 leading-tight">
          {{ $t('intentPages.viberBusinessMessagesProvider.heroTitle') }}
        </h1>
        <p class="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-8 leading-relaxed">
          {{ $t('intentPages.viberBusinessMessagesProvider.heroSubtitle') }}
        </p>
        <div class="flex justify-center flex-wrap gap-4 mb-10">
          <div v-for="(highlight, index) in $tm('intentPages.viberBusinessMessagesProvider.heroHighlights')" :key="index" class="bg-indigo-200/50 text-indigo-800 px-4 py-2 rounded-full font-bold text-sm tracking-wide">
             <i class="pi pi-check mr-1"></i> {{ $rt(highlight) }}
          </div>
        </div>
        <div class="flex justify-center flex-wrap gap-4">
          <a href="https://cabinet.smsbat.com/" class="nav-link button filled px-10 py-5 text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition text-white bg-[#3070b6] hover:bg-blue-700">
             {{ $t('intentPages.viberBusinessMessagesProvider.heroCta1') }}
          </a>
          <NuxtLink :to="localePath('/pricing/viber/' + ($route.params.country || 'ua'))" class="nav-link button outlined px-10 py-5 text-xl font-bold rounded-xl border-2 border-[#3070b6] text-[#3070b6] bg-transparent hover:bg-blue-50 transition">
             {{ $t('intentPages.viberBusinessMessagesProvider.heroCta2') }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- What are Viber Business Messages? (Definition) -->
    <section class="page-section pt-10 pb-0 bg-white">
      <div class="container mx-auto max-w-5xl px-4 text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.definitionTitle') }}</h2>
        <p class="text-xl text-gray-700 mb-8">{{ $t('intentPages.viberBusinessMessagesProvider.definitionText') }}</p>
        <p class="text-lg font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.definitionSubtitle') }}</p>
        <div class="flex flex-wrap justify-center gap-4 mb-8">
           <NuxtLink :to="localePath('/viber-transactional-messages')" class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition">
             {{ $t('intentPages.viberBusinessMessagesProvider.definitionList[0]') }}
           </NuxtLink>
           <NuxtLink :to="localePath('/viber-otp')" class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium hover:bg-blue-100 transition">
             {{ $t('intentPages.viberBusinessMessagesProvider.definitionList[1]') }}
           </NuxtLink>
           <div class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium">
             {{ $t('intentPages.viberBusinessMessagesProvider.definitionList[2]') }}
           </div>
           <div class="bg-blue-50 text-blue-800 px-6 py-3 rounded-lg font-medium">
             {{ $t('intentPages.viberBusinessMessagesProvider.definitionList[3]') }}
           </div>
        </div>
        <p class="text-lg text-gray-600 italic">{{ $t('intentPages.viberBusinessMessagesProvider.definitionSummary') }}</p>
      </div>
    </section>

    <!-- Types of Viber Business Messages -->
    <section class="page-section pt-8 pb-10 bg-gray-50 border-t border-gray-100">
      <div class="container mx-auto max-w-6xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.typesTitle') }}</h2>
          <p class="text-xl text-gray-600">{{ $t('intentPages.viberBusinessMessagesProvider.typesSummary') }}</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Transactional -->
          <div class="bg-white p-8 rounded-2xl shadow-sm border border-gray-200">
            <div class="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-[#3070b6] text-2xl mb-6">
              <i class="pi pi-receipt"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.types.transactional.title') }}</h3>
            <p class="font-medium text-gray-700 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.types.transactional.subtitle') }}</p>
            <ul class="space-y-3">
              <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.types.transactional.list')" :key="index" class="flex items-start text-gray-600">
                <i class="pi pi-check text-green-500 mt-1 mr-3"></i> {{ $rt(item) }}
              </li>
            </ul>
          </div>
          <!-- Promotional -->
          <div class="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 border-t-4 border-t-purple-500">
            <div class="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 text-2xl mb-6">
              <i class="pi pi-megaphone"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.types.promotional.title') }}</h3>
            <p class="font-medium text-gray-700 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.types.promotional.subtitle') }}</p>
            <ul class="space-y-3">
              <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.types.promotional.list')" :key="index" class="flex items-start text-gray-600">
                <i class="pi pi-check text-green-500 mt-1 mr-3"></i> {{ $rt(item) }}
              </li>
            </ul>
          </div>
          <!-- Conversational -->
          <div class="bg-white p-8 rounded-2xl shadow-sm border border-gray-200 border-t-4 border-t-[#3070b6]">
            <div class="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-[#3070b6] text-2xl mb-6">
              <i class="pi pi-comments"></i>
            </div>
            <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.types.conversational.title') }}</h3>
            <p class="font-medium text-gray-700 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.types.conversational.subtitle') }}</p>
            <ul class="space-y-3">
              <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.types.conversational.list')" :key="index" class="flex items-start text-gray-600">
                <i class="pi pi-check text-green-500 mt-1 mr-3"></i> {{ $rt(item) }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Visual Showcase of Message Types -->
    <section class="page-section py-10 bg-white">
      <div class="container mx-auto max-w-7xl px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.showcaseTitle') }}</h2>
          <p class="text-xl text-gray-600">{{ $t('intentPages.viberBusinessMessagesProvider.showcaseSummary') }}</p>
        </div>

        <!-- 4-Column Showcase Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.showcaseItems')" :key="index" class="group perspective-1000 flex flex-col items-center">
            <div class="bg-[#f0f2f5] rounded-3xl p-4 w-full shadow-md transition-all duration-700 ease-out hover:-translate-y-3 hover:rotate-y-3 hover:shadow-2xl relative overflow-hidden border border-gray-200">
               <!-- Subtle highlight effect -->
               <div class="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none"></div>
               <NuxtImg :src="`/images/viber-types/${$rt(item.img)}`" :alt="$rt(item.title)" :width="400" :height="400" format="webp" loading="lazy" class="w-full h-auto rounded-xl drop-shadow-sm" />
            </div>
            <h3 class="mt-6 text-lg font-bold text-gray-800 text-center">{{ $rt(item.title) }}</h3>
          </div>
        </div>
      </div>
    </section>

    <!-- Viber vs SMS Comparison -->
    <section class="page-section py-10 bg-white">
      <div class="container mx-auto max-w-4xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTitle') }}</h2>
          <p class="text-xl text-gray-600">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonText') }}</p>
        </div>
        
        <div class="overflow-x-auto rounded-xl shadow-sm border border-gray-200">
          <table class="w-full text-left border-collapse min-w-[500px]">
            <thead>
              <tr class="bg-gray-100 text-gray-800 text-lg">
                <th class="p-4 font-bold border-b border-gray-200">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.headers[0]') }}</th>
                <th class="p-4 font-bold border-b border-gray-200 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.headers[1]') }}</th>
                <th class="p-4 font-bold border-b border-gray-200 text-center text-[#7360f2]">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.headers[2]') }} <i class="pi pi-verified ml-1"></i></th>
              </tr>
            </thead>
            <tbody class="text-lg">
              <tr class="border-b border-gray-100">
                <td class="p-4 font-medium">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.branding[0]') }}</td>
                <td class="p-4 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.branding[1]') }}</td>
                <td class="p-4 text-center text-green-600">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.branding[2]') }}</td>
              </tr>
              <tr class="border-b border-gray-100 bg-gray-50/50">
                <td class="p-4 font-medium">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.images[0]') }}</td>
                <td class="p-4 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.images[1]') }}</td>
                <td class="p-4 text-center text-green-600">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.images[2]') }}</td>
              </tr>
              <tr class="border-b border-gray-100">
                <td class="p-4 font-medium">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.buttons[0]') }}</td>
                <td class="p-4 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.buttons[1]') }}</td>
                <td class="p-4 text-center text-green-600">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.buttons[2]') }}</td>
              </tr>
              <tr class="border-b border-gray-100 bg-gray-50/50">
                <td class="p-4 font-medium">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.cost[0]') }}</td>
                <td class="p-4 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.cost[1]') }}</td>
                <td class="p-4 text-center font-bold text-green-600">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.cost[2]') }}</td>
              </tr>
              <tr>
                <td class="p-4 font-medium">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.engagement[0]') }}</td>
                <td class="p-4 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.engagement[1]') }}</td>
                <td class="p-4 text-center font-bold text-green-600">{{ $t('intentPages.viberBusinessMessagesProvider.comparisonTable.engagement[2]') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Use Cases & Example Message -->
    <section class="page-section py-10 bg-gray-50 border-t border-gray-100">
      <div class="container mx-auto max-w-7xl px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900">{{ $t('intentPages.viberBusinessMessagesProvider.useCasesTitle') }}</h2>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <!-- Use Cases List -->
          <div class="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- eCommerce -->
            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i class="pi pi-shopping-cart text-[#3070b6]"></i> {{ $t('intentPages.viberBusinessMessagesProvider.useCases.ecommerce.title') }}
              </h3>
              <ul class="space-y-2">
                <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.useCases.ecommerce.list')" :key="index" class="text-gray-600 flex items-center gap-2">
                  <i class="pi pi-circle-fill text-[8px] text-gray-400 mt-1"></i> {{ $rt(item) }}
                </li>
              </ul>
            </div>
            <!-- Banking -->
            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i class="pi pi-building text-[#3070b6]"></i> {{ $t('intentPages.viberBusinessMessagesProvider.useCases.banking.title') }}
              </h3>
              <ul class="space-y-2">
                <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.useCases.banking.list')" :key="index" class="text-gray-600 flex items-center gap-2">
                  <i class="pi pi-circle-fill text-[8px] text-gray-400 mt-1"></i> {{ $rt(item) }}
                </li>
              </ul>
            </div>
            <!-- Logistics -->
            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i class="pi pi-truck text-[#3070b6]"></i> {{ $t('intentPages.viberBusinessMessagesProvider.useCases.logistics.title') }}
              </h3>
              <ul class="space-y-2">
                <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.useCases.logistics.list')" :key="index" class="text-gray-600 flex items-center gap-2">
                  <i class="pi pi-circle-fill text-[8px] text-gray-400 mt-1"></i> {{ $rt(item) }}
                </li>
              </ul>
            </div>
            <!-- Support -->
            <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i class="pi pi-users text-[#3070b6]"></i> {{ $t('intentPages.viberBusinessMessagesProvider.useCases.support.title') }}
              </h3>
              <ul class="space-y-2">
                <li v-for="(item, index) in $tm('intentPages.viberBusinessMessagesProvider.useCases.support.list')" :key="index" class="text-gray-600 flex items-center gap-2">
                  <i class="pi pi-circle-fill text-[8px] text-gray-400 mt-1"></i> {{ $rt(item) }}
                </li>
              </ul>
            </div>
          </div>
          
          <!-- Example Message Mockup -->
          <div class="lg:col-span-4 flex flex-col items-center">
             <h3 class="text-xl font-bold text-gray-900 mb-6 text-center">{{ $t('intentPages.viberBusinessMessagesProvider.exampleTitle') }}</h3>
             <div class="bg-[#f0f2f5] w-full max-w-sm rounded-[1rem] p-4 shadow-xl border border-gray-300 relative">
                <!-- Phone top bar purely visual -->
                <div class="w-16 h-1 bg-gray-300 rounded-full mx-auto mb-4"></div>
                <!-- Mockup Header -->
                <div class="flex items-center gap-3 mb-4 bg-white p-3 rounded-xl shadow-sm">
                   <div class="w-10 h-10 bg-[#3070b6] rounded-full flex items-center justify-center text-white"><i class="pi pi-box"></i></div>
                   <div class="flex-1">
                     <p class="font-bold text-sm text-gray-900">Global Delivery</p>
                     <p class="text-[10px] text-gray-500 line-clamp-1"><i class="pi pi-verified text-green-500 mr-1"></i>Verified Service</p>
                   </div>
                </div>
                <!-- Mockup Message -->
                <div class="bg-white p-4 rounded-2xl rounded-tl-sm shadow-sm mb-2 text-sm text-gray-800">
                   <p class="mb-3 font-medium">📦 {{ $t('intentPages.viberBusinessMessagesProvider.exampleContent.text1') }}</p>
                   <p class="mb-1 text-gray-600">{{ $t('intentPages.viberBusinessMessagesProvider.exampleContent.text2') }}</p>
                   <p class="text-blue-500 underline mb-3">{{ $t('intentPages.viberBusinessMessagesProvider.exampleContent.link') }}</p>
                   <p class="text-xs text-gray-500">{{ $t('intentPages.viberBusinessMessagesProvider.exampleContent.text3') }}</p>
                </div>
                <div class="bg-[#3070b6] text-white text-center py-2.5 rounded-xl text-sm font-bold mt-3 shadow-sm cursor-pointer hover:bg-blue-700">Track Order</div>
             </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How SMSBAT Viber Messaging Works -->
    <section class="page-section py-10 bg-[#3070b6] text-white">
      <div class="container mx-auto max-w-6xl px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.howItWorksTitle') }}</h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
          <!-- Connector line for desktop -->
          <div class="hidden md:block absolute top-[28px] left-[12%] right-[12%] h-1 bg-blue-400 z-0"></div>
          
          <div v-for="(step, index) in $tm('intentPages.viberBusinessMessagesProvider.howItWorksSteps')" :key="index" class="flex flex-col items-center text-center relative z-10 w-full px-2">
            <div class="w-14 h-14 bg-white rounded-full flex items-center justify-center text-[#3070b6] font-black text-2xl mb-6 shadow-lg border-4 border-[#3070b6]">
              {{ Number(index) + 1 }}
            </div>
            <p class="text-blue-100 font-medium text-lg leading-tight">{{ $rt(step) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Countries Supported -->
    <section class="page-section py-10 bg-white border-b border-gray-100">
      <div class="container mx-auto max-w-5xl px-4 text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.countriesTitle') }}</h2>
        <p class="text-xl text-gray-700 mb-10">{{ $t('intentPages.viberBusinessMessagesProvider.countriesText') }}</p>
        
        <div class="flex flex-wrap justify-center gap-4 mb-8">
           <div v-for="(country, index) in $tm('intentPages.viberBusinessMessagesProvider.countriesList')" :key="index" class="bg-gray-100 hover:bg-gray-200 transition text-gray-800 px-6 py-2.5 rounded-full font-medium shadow-sm">
             {{ $rt(country) }}
           </div>
        </div>
        <p class="text-lg text-gray-600 font-medium">... {{ $t('intentPages.viberBusinessMessagesProvider.countriesSuffix') }}</p>
      </div>
    </section>

    <!-- Mobile App Showcase -->
    <div class="page-section pt-16 pb-20 overflow-hidden relative border-t border-gray-100">
      <div class="absolute inset-0 bg-blue-50/30 skew-y-1 origin-top-left -z-10"></div>
      <div class="container mx-auto px-4 max-w-7xl">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberBusinessMessagesProvider.mobileAppTitle') }}</h2>
            <p class="text-xl text-gray-700 mb-4">{{ $t('intentPages.viberBusinessMessagesProvider.mobileAppLine1') }}</p>
            <p class="text-lg text-gray-600 leading-relaxed mb-10">{{ $t('intentPages.viberBusinessMessagesProvider.mobileAppLine2') }}</p>

            <div class="flex items-center gap-4 flex-wrap">
              <!-- Google Play -->
              <a href="https://play.google.com/store/apps/details?id=com.smsbat.chat" target="_blank" rel="noopener noreferrer" class="transition-transform hover:-translate-y-1 inline-block">
                <NuxtImg src="/images/google-play-badge-en.svg" width="180" height="53" alt="Get it on Google Play" format="webp" loading="lazy" class="h-[40px] md:h-[50px] w-auto drop-shadow-sm" />
              </a>
              
              <!-- App Store (Coming Soon) -->
              <div class="relative inline-block transition-transform hover:-translate-y-1 cursor-default group focus:outline-none">
                <NuxtImg src="/images/app-store-badge.svg" width="120" height="40" alt="Download on the App Store" format="webp" loading="lazy" class="h-[40px] md:h-[50px] w-auto drop-shadow-sm transition-all duration-300" />
                <div class="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-blue-600 text-white text-[10px] md:text-sm font-bold px-2 md:px-3 py-1 rounded-full shadow-lg z-10 whitespace-nowrap border-2 border-white pointer-events-none transition-all duration-300 group-hover:scale-110">
                  {{ $i18n.locale === 'uk-ua' ? 'Скоро' : 'Coming Soon' }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-center group perspective-1000">
            <NuxtImg src="/images/chat/omnichannel-app.png" width="571" height="1024" format="webp" loading="lazy" alt="SMSBAT Omnichannel Contact Center App" class="w-full max-w-[320px] h-auto rounded-[2rem] shadow-2xl transition-all duration-700 ease-out hover:-translate-y-4 hover:rotate-y-6" />
          </div>
        </div>
      </div>
    </div>

    <!-- How To Start Funnel -->
    <HowToStart channel="viber-business-messages" country="" />

    <!-- FAQ Section -->
    <div class="page-section py-10 bg-gray-50 border-t border-gray-100">
      <div class="container mx-auto max-w-4xl px-4">
        <PopularQuestions :title="$t('intentPages.viberBusinessMessagesProvider.faqTitle')" :questions="faqItems" />
      </div>
    </div>
    
  </div>
</template>

<style scoped lang="scss">
.provider-intent-page {
  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
